import { MediaEl } from '@/components/media/types';
import { MediaGuideMetadata } from '@/utils/media/types';
import { getGuideId } from '../media';

export function sortGuides(a: MediaGuideMetadata, b: MediaGuideMetadata) {
    if (a.position === b.position) {
        return 0;
    }
    return a.position > b.position ? 1 : -1;
}

export function getFirstSession(orderedSessions: MediaGuideMetadata[]) {
    return orderedSessions.find((session) => !session.isTrailer);
}

export function isMindshiftGuide(guide?: MediaGuideMetadata | null): boolean {
    return (
        guide?.metadata?.custom_content_type ===
        GuideMetadataCustomContentType.Mindshift
    );
}

export function getRelatedContentPackClass(guide: MediaGuideMetadata) {
    return isMindshiftGuide(guide)
        ? `mindshift-${getGuideId(guide).toLowerCase()}`
        : undefined;
}

export enum GuideMetadataCustomContentType {
    Mindshift = 'mindshift',
}

export function averagePackGuideMinutes(guides: MediaGuideMetadata[]) {
    if (!guides || guides.length === 0) {
        return { min: 0, max: 0 };
    }

    const { min, max } = guides.reduce(
        (acc, guide) => {
            if (guide.isTrailer) return acc; // skip trailer

            if (!acc.min || guide.duration < acc.min) acc.min = guide.duration;
            if (!acc.max || guide.duration > acc.max) acc.max = guide.duration;

            return acc;
        },
        { min: 0, max: 0 }
    );

    return { min, max };
}

const stripQuery = (url?: string) => (url ? url.split('?')[0] : '');

/**
 * True when `guide` is the audio identified by `activeSrc`, compared by asset
 * URL (query string stripped — playback URLs carry signed credentials).
 *
 * `activeSrc` is the low-frequency `currentSrc` snapshot promoted to React
 * state in `useMediaControls` (set on load/track-change, not on `timeupdate`).
 * Comparing against it keeps active-track detection a pure function of state,
 * so consumers don't re-render at the ~4Hz `timeupdate` cadence. EVE-12668.
 */
export function isGuideActiveForSrc(
    guide: MediaGuideMetadata | undefined,
    activeSrc: string | undefined
): boolean {
    const guideUrl = stripQuery(guide?.assets?.[0]?.url);
    const mediaUrl = stripQuery(activeSrc);
    return !!guideUrl && guideUrl === mediaUrl;
}

/**
 * True when `guide` is the audio currently loaded into `mediaEl`. Thin wrapper
 * over {@link isGuideActiveForSrc} for callers that hold the element (e.g. the
 * header Play CTA) rather than the promoted `activeSrc` state.
 */
export function isActiveGuide(
    guide: MediaGuideMetadata | undefined,
    mediaEl: MediaEl | undefined
): boolean {
    return isGuideActiveForSrc(guide, mediaEl?.currentSrc);
}
